import { useEffect, useRef, useState } from 'react';
import api from '../../../services/api';

/**
 * El expediente entero del deudor al que se está llamando.
 *
 * Se pide **en cuanto entra la marcación**, no al descolgar: los ~22 segundos de
 * tono son el briefing del gestor, y si llegara con la voz del deudor llegaría
 * tarde. Es la misma razón por la que la ficha corta ya se pide ahí.
 *
 * 🔑 **Se usa el MISMO endpoint que la pantalla de expedientes**, filtrando por
 * `expedient_id`. No es comodidad: los paneles que pinta el puesto del gestor
 * —`ManagementPanel`, `ExpedientDetailsPanel`— son los de esa pantalla, y
 * cualquier diferencia de forma entre los dos objetos aparecería como un campo
 * vacío en una llamada real, no en un test.
 *
 * Se piden los tres estados porque durante la llamada la tarea está
 * `in_progress`; con el `pending` por defecto no volvería nada.
 */
const ESTADOS = ['pending', 'in_progress', 'completed'];

export default function useExpedienteDeLaLlamada(expedientId) {
  const [expediente, setExpediente] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [tardanza, setTardanza] = useState(null);

  // Para descartar respuestas de una llamada anterior: con el marcador en
  // marcha, una petición lenta puede volver cuando el gestor ya está con otro
  // deudor, y pintar la ficha equivocada mientras habla es peor que no pintarla.
  const ultimaPedida = useRef(null);

  useEffect(() => {
    if (!expedientId) {
      setExpediente(null);
      return undefined;
    }

    let cancelado = false;
    ultimaPedida.current = expedientId;
    setCargando(true);

    const arranque = performance.now();
    const query = `expedient_id=${encodeURIComponent(expedientId)}&${ESTADOS.map((e) => `status=${e}`).join('&')}&limit=1`;

    api
      .get(`/assignments/expedients?${query}`)
      .then(({ data }) => {
        if (cancelado || ultimaPedida.current !== expedientId) return;
        setExpediente(data?.expedients?.[0] || null);
        // Cuánto cuesta traerlo. Si compite con el audio se verá aquí, y el
        // remedio sería moverlo al descuelgue.
        setTardanza(Math.round(performance.now() - arranque));
      })
      .catch(() => {
        // Sin expediente el gestor sigue teniendo el briefing corto y la
        // llamada: un fallo aquí no puede dejarle sin atender a nadie.
        if (!cancelado) setExpediente(null);
      })
      .finally(() => {
        if (!cancelado) setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [expedientId]);

  return { expediente, cargando, tardanza };
}
